/**
 * React Flow 自定义节点：RemixCard（简版 PromptCard）
 * 单 textarea 自由编辑完整提示词 + 生成按钮
 * 生成的图片成为本卡的子 ImageNode，纵向 reflow
 */

import { Handle, Position, useReactFlow, type NodeProps, type Node } from '@xyflow/react'
import { useCallback } from 'react'
import { canvasApi } from '../../utils/canvas-api'
import { useCanvasStore } from '../../stores/canvas'
import { useConfirmStore } from './ConfirmDialog'
import { reflowChildren, CHILD_X } from './canvas-layout'

export interface RemixCardData extends Record<string, unknown> {
  prompt: string
  isGenerating: boolean
  lastError: string
  collapsed?: boolean
  model?: string
  seed?: string
  batchCount?: 1 | 2 | 4
  generationProgress?: string
}

export type RemixCardNodeType = Node<RemixCardData, 'prompt-remix'>

export const REMIX_CARD_DEFAULT_DATA: RemixCardData = {
  prompt: '',
  isGenerating: false,
  lastError: '',
  collapsed: false,
  model: 'black-forest-labs/flux-schnell',
  seed: '',
  batchCount: 1,
  generationProgress: '',
}

const AVAILABLE_IMAGE_MODELS = [
  { value: 'black-forest-labs/flux-schnell', label: 'Flux Schnell' },
  { value: 'black-forest-labs/flux-2-dev', label: 'Flux 2 Dev' },
  { value: 'prunaai/p-image', label: 'P-Image' },
  { value: 'prunaai/z-image-turbo', label: 'Z-Image Turbo' },
]

const BATCH_OPTIONS: Array<1 | 2 | 4> = [1, 2, 4]

export function RemixCardNode({ id, data, selected }: NodeProps<RemixCardNodeType>) {
  const rf = useReactFlow()
  const {
    prompt,
    isGenerating,
    lastError,
    collapsed = false,
    model = 'black-forest-labs/flux-schnell',
    seed = '',
    batchCount = 1,
    generationProgress = '',
  } = data

  const updateData = useCallback(
    (patch: Partial<RemixCardData>) => {
      rf.setNodes((nodes) =>
        nodes.map((n) => (n.id === id ? { ...n, data: { ...(n.data as RemixCardData), ...patch } } : n)),
      )
    },
    [rf, id],
  )

  const handleDelete = useCallback(() => {
    const childCount = rf.getEdges().filter((e) => e.source === id).length
    useConfirmStore.getState().show({
      title: '删除 Remix 卡片',
      message:
        childCount > 0
          ? `这张卡片下有 ${childCount} 张图片，删除后图片将一并被移除，确认？`
          : '确认删除此 Remix 卡片？',
      confirmText: '删除',
      danger: true,
      onConfirm: () => {
        const childIds = rf.getNodes().filter((n) => n.parentId === id).map((n) => ({ id: n.id }))
        rf.deleteElements({ nodes: [{ id }, ...childIds] })
      },
    })
  }, [rf, id])

  const handleToggle = useCallback(() => {
    updateData({ collapsed: !collapsed })
  }, [updateData, collapsed])

  const handleDuplicate = useCallback(() => {
    const self = rf.getNode(id)
    if (!self) return
    const newRemixId = `remix_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    const nextData: RemixCardData = {
      ...(self.data as RemixCardData),
      isGenerating: false,
      lastError: '',
      generationProgress: '',
    }
    rf.setNodes((nodes) => [
      ...nodes,
      {
        id: newRemixId,
        type: 'prompt-remix',
        position: { x: self.position.x + 430, y: self.position.y + 24 },
        dragHandle: '.drag-handle',
        data: nextData,
      },
    ])
  }, [id, rf])

  const handleGenerate = useCallback(async () => {
    if (isGenerating) return
    const trimmed = prompt.trim()
    if (!trimmed) {
      updateData({ lastError: '请先输入提示词' })
      return
    }

    const normalizedSeed = seed.trim()
    const baseSeed: number | null = normalizedSeed ? Number(normalizedSeed) : null
    if (baseSeed !== null && (!Number.isInteger(baseSeed) || baseSeed < 0)) {
      updateData({ lastError: 'Seed 必须是非负整数' })
      return
    }

    const count = BATCH_OPTIONS.includes(batchCount) ? batchCount : 1
    updateData({ isGenerating: true, lastError: '', generationProgress: count > 1 ? `准备生成 ${count} 张图片…` : '' })

    try {
      const canvasId = useCanvasStore.getState().canvasId
      const existingChildCount = rf.getNodes().filter((n) => n.parentId === id && n.type === 'image-node').length

      for (let index = 0; index < count; index++) {
        updateData({
          generationProgress: count > 1 ? `正在生成第 ${index + 1}/${count} 张图片…` : '',
        })
        const requestSeed = baseSeed !== null ? baseSeed + index : count > 1 ? Math.floor(Math.random() * 1000000) : undefined
        const result = await canvasApi.generate({
          fields: { subject: trimmed },
          canvas_id: canvasId || undefined,
          model,
          seed: requestSeed,
        })

        const offsetY = (existingChildCount + index) * 392
        const newId = `image_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

        rf.setNodes((nodes) => [
          ...nodes,
          {
            id: newId,
            type: 'image-node',
            parentId: id,
            position: { x: CHILD_X, y: offsetY },
            dragHandle: '.drag-handle',
            data: {
              imageUrl: result.url,
              prompt: result.prompt,
              generationId: result.id,
              model: result.model,
              seed: requestSeed,
              collapsed: false,
            },
          },
        ])

        rf.setEdges((edges) => [
          ...edges,
          {
            id: `${id}->${newId}`,
            source: id,
            target: newId,
            animated: true,
            style: { stroke: '#8b5cf6', strokeWidth: 2 },
          },
        ])
      }

      reflowChildren(rf, id)
      updateData({ isGenerating: false, lastError: '', generationProgress: '' })
    } catch (err) {
      console.error('Remix generate failed:', err)
      updateData({
        isGenerating: false,
        generationProgress: '',
        lastError: err instanceof Error ? err.message : '生成失败',
      })
    }
  }, [batchCount, id, isGenerating, model, prompt, rf, seed, updateData])

  return (
    <>
      <Handle type="target" position={Position.Left} style={{ background: '#06b6d4', width: 10, height: 10 }} />
      <div
        style={{
          width: 380,
          background: '#0f172a',
          color: '#e2e8f0',
          borderRadius: 12,
          boxShadow: selected
            ? '0 0 0 2px #06b6d4, 0 4px 24px rgba(0,0,0,0.45)'
            : '0 4px 24px rgba(0,0,0,0.35)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 12,
          overflow: 'hidden',
          border: '1px solid #1e293b',
        }}
      >
        <div
          className="drag-handle"
          style={{
            padding: '10px 14px',
            background: 'linear-gradient(90deg, #06b6d4, #6366f1)',
            color: '#fff',
            fontWeight: 600,
            letterSpacing: 0.3,
            cursor: 'move',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ flex: 1 }}>Remix · 再编辑</span>
          <button
            type="button"
            className="nodrag"
            onClick={handleDuplicate}
            onMouseDown={(e) => e.stopPropagation()}
            title="复制卡片"
            style={{
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.35)',
              color: '#fff',
              width: 22,
              height: 22,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 12,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            ⧉
          </button>
          <button
            type="button"
            className="nodrag"
            onClick={handleToggle}
            onMouseDown={(e) => e.stopPropagation()}
            title={collapsed ? '展开' : '最小化'}
            style={{
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.35)',
              color: '#fff',
              width: 22,
              height: 22,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            {collapsed ? '+' : '−'}
          </button>
          <button
            type="button"
            className="nodrag"
            onClick={handleDelete}
            onMouseDown={(e) => e.stopPropagation()}
            title="删除卡片"
            style={{
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.35)',
              color: '#fff',
              width: 22,
              height: 22,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 12,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            ✕
          </button>
        </div>

        {!collapsed && (
          <>
        <div className="nodrag" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: '#94a3b8', fontSize: 11 }}>📜 完整提示词</span>
            <textarea
              value={prompt}
              onChange={(e) => updateData({ prompt: e.target.value })}
              placeholder="编辑完整提示词…"
              rows={6}
              style={{
                background: '#020617',
                border: '1px solid #334155',
                borderRadius: 6,
                padding: 8,
                color: '#f1f5f9',
                fontSize: 12,
                fontFamily: 'JetBrains Mono, monospace',
                resize: 'vertical',
                minHeight: 100,
                outline: 'none',
                lineHeight: 1.5,
              }}
            />
          </label>

          {generationProgress ? <div style={{ color: '#67e8f9', fontSize: 11 }}>{generationProgress}</div> : null}
          {lastError ? <div style={{ color: '#f87171', fontSize: 11 }}>{lastError}</div> : null}
        </div>

        <div className="nodrag" style={{ padding: 10, borderTop: '1px solid #1e293b', background: '#0b1120', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 78px 92px', gap: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: '#64748b', fontSize: 10 }}>模型</span>
              <select
                value={model}
                onChange={(e) => updateData({ model: e.target.value })}
                style={{
                  background: '#020617',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  color: '#cbd5e1',
                  padding: '6px 8px',
                  fontSize: 11,
                  outline: 'none',
                }}
              >
                {AVAILABLE_IMAGE_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: '#64748b', fontSize: 10 }}>数量</span>
              <select
                value={batchCount}
                onChange={(e) => updateData({ batchCount: Number(e.target.value) as 1 | 2 | 4 })}
                style={{
                  background: '#020617',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  color: '#cbd5e1',
                  padding: '6px 8px',
                  fontSize: 11,
                  outline: 'none',
                }}
              >
                {BATCH_OPTIONS.map((count) => (
                  <option key={count} value={count}>{count} 张</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: '#64748b', fontSize: 10 }}>Seed</span>
              <input
                value={seed}
                onChange={(e) => updateData({ seed: e.target.value.replace(/[^\d]/g, '') })}
                placeholder="随机"
                inputMode="numeric"
                style={{
                  background: '#020617',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  color: '#cbd5e1',
                  padding: '6px 8px',
                  fontSize: 11,
                  outline: 'none',
                }}
              />
            </label>
          </div>
          <button
            type="button"
            disabled={isGenerating}
            onClick={handleGenerate}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: 'none',
              borderRadius: 8,
              background: isGenerating ? '#475569' : 'linear-gradient(90deg, #06b6d4, #6366f1)',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              letterSpacing: 0.5,
            }}
          >
            {isGenerating ? (batchCount > 1 ? '⏳ 批量生成中…' : '⏳ 生成中…') : `⚡ 生成 ${batchCount} 张图片`}
          </button>
        </div>
          </>
        )}
      </div>
      <Handle type="source" position={Position.Right} style={{ background: '#06b6d4', width: 10, height: 10 }} />
    </>
  )
}
